import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { MonthlyData } from '../hooks/useAnalyticsData';

interface Props {
  data: MonthlyData[];
  loading: boolean;
}

function formatY(v: number): string {
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
  return String(Math.round(v));
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const v = Number(payload[0]?.value ?? 0);
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-2.5 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      <p className="text-violet-600 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" />
        月均客单价：¥{v.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}
      </p>
    </div>
  );
}

export default function AvgOrderValueTrend({ data, loading }: Props) {
  const hasAny = data.some(d => d.avgIncomeValue > 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">月均客单价趋势</h3>
          <p className="text-xs text-gray-400 mt-0.5">收入订单的月度平均金额走势</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-3 h-0.5 bg-violet-500 rounded-full inline-block" />
          客单价
        </span>
      </div>
      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="text-sm text-gray-400 animate-pulse">加载中...</div>
        </div>
      ) : !hasAny ? (
        <div className="h-48 flex flex-col items-center justify-center text-gray-300">
          <i className="ri-bar-chart-2-line text-3xl mb-2" />
          <span className="text-xs">暂无足够数据</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatY} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={48} />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="avgIncomeValue"
              name="月均客单价"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={{ r: 3, fill: '#8b5cf6' }}
              activeDot={{ r: 5, fill: '#8b5cf6' }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
